import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from '@/components/ui/command';
import { supabase } from '@/integrations/supabase/client';
import { useAgency } from '@/contexts/AgencyContext';
import { useActiveBrand } from '@/contexts/BrandContext';
import {
  Building2, Users, Radio, Megaphone, FileText, Calendar, LayoutDashboard,
  Zap, Sparkles, Upload, Search, ArrowRight, Bell, ClipboardList, Package,
  Wallet, Briefcase, Rocket, Activity,
} from 'lucide-react';

/**
 * Universal command palette. Cmd/Ctrl+K opens it from anywhere in the app.
 *
 * Design goals:
 *   - Actions first — the most common intents live at the top so the palette
 *     works as a keyboard-driven action bar, not just a search.
 *   - Live server search across brands / campaigns / creators / LIVE sessions
 *     with a 150ms debounce so we don't hammer Supabase on every keystroke.
 *   - Brand rows have a secondary "→ Switch brand" affordance so you can
 *     scope the app from anywhere without leaving the current page.
 */

type Nav = { title: string; url: string; icon: any };

const NAV_ITEMS: Nav[] = [
  { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard },
  { title: 'Inbox', url: '/inbox', icon: Activity },
  { title: 'Brands', url: '/brands', icon: Building2 },
  { title: 'Creators', url: '/creators', icon: Users },
  { title: 'Campaigns', url: '/campaigns', icon: Megaphone },
  { title: 'Contracts', url: '/contracts', icon: FileText },
  { title: 'Creator Payouts', url: '/creator-payouts', icon: Wallet },
  { title: 'CRM', url: '/crm', icon: Briefcase },
  { title: 'Notifications', url: '/settings/notifications', icon: Bell },
];

function useDebounced(value: string, delay = 150) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 150);
  const navigate = useNavigate();
  const { agencyId, isSuperAdmin } = useAgency();
  const { setBrand } = useActiveBrand();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const { data: results } = useQuery({
    queryKey: ['command-palette', debounced, agencyId, isSuperAdmin],
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
    queryFn: async () => {
      const safe = debounced.trim().replace(/[(),]/g, ' ').replace(/[%_]/g, '\\$&');
      const q = `%${safe}%`;
      const brandsQ = supabase
        .from('brands')
        .select('id, name, logo_url')
        .ilike('name', q)
        .is('deleted_at', null)
        .limit(6);
      const campaignsQ = supabase.from('campaigns').select('id, name, brand_id').ilike('name', q).limit(6);
      const creatorsQ = supabase
        .from('affiliates')
        .select('id, tiktok_handle, full_name, brand_id')
        .or(`tiktok_handle.ilike.${q},full_name.ilike.${q}`)
        .limit(6);
      const sessionsQ = supabase
        .from('live_sessions')
        .select('id, stream_title, brand_id, scheduled_at')
        .ilike('stream_title', q)
        .limit(6);
      const [brands, campaigns, creators, sessions] = await Promise.all([brandsQ, campaignsQ, creatorsQ, sessionsQ]);
      return {
        brands: brands.data ?? [],
        campaigns: campaigns.data ?? [],
        creators: creators.data ?? [],
        sessions: sessions.data ?? [],
      };
    },
  });

  const go = (path: string) => { setOpen(false); navigate(path); };

  const navFiltered = useMemo(() => {
    if (!query) return NAV_ITEMS;
    const q = query.toLowerCase();
    return NAV_ITEMS.filter((n) => n.title.toLowerCase().includes(q));
  }, [query]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <div className="flex items-center border-b px-3">
        <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
        <CommandInput
          placeholder="Search brands, creators, campaigns, or type an action…"
          value={query}
          onValueChange={setQuery}
          className="border-0 focus:ring-0"
        />
      </div>
      <CommandList className="max-h-[440px]">
        <CommandEmpty>No matches — try another term.</CommandEmpty>

        <CommandGroup heading="Quick actions">
          <CommandItem onSelect={() => go('/brands')} className="gap-2">
            <Radio className="h-4 w-4 text-primary" /> Schedule a LIVE (pick brand)
          </CommandItem>
          <CommandItem onSelect={() => go('/campaigns?new=1')} className="gap-2">
            <Megaphone className="h-4 w-4 text-primary" /> New campaign
          </CommandItem>
          <CommandItem onSelect={() => go('/brands?new=1')} className="gap-2">
            <Building2 className="h-4 w-4 text-primary" /> Add brand
          </CommandItem>
          <CommandItem onSelect={() => go('/brands')} className="gap-2">
            <Upload className="h-4 w-4 text-primary" /> Upload LIVE report (pick brand)
          </CommandItem>
          <CommandItem onSelect={() => { setBrand(null); setOpen(false); }} className="gap-2">
            <Building2 className="h-4 w-4" /> Reset brand scope to All brands
          </CommandItem>
        </CommandGroup>

        {results?.brands.length ? (
          <>
            <CommandSeparator />
            <CommandGroup heading="Brands">
              {results.brands.map((b: any) => (
                <CommandItem key={`b-${b.id}`} value={`brand ${b.name}`} className="gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{b.name}</span>
                  <button
                    className="text-[10px] px-1.5 py-0.5 rounded bg-muted hover:bg-primary/20 text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                    onClick={(e) => { e.stopPropagation(); setBrand(b.id); setOpen(false); }}
                    title="Set as active brand"
                  >
                    <ArrowRight className="h-3 w-3" /> Switch
                  </button>
                  <button
                    className="text-[10px] px-1.5 py-0.5 rounded bg-muted hover:bg-primary/20 text-muted-foreground hover:text-primary"
                    onClick={(e) => { e.stopPropagation(); go(`/brands/${b.id}`); }}
                  >
                    Open
                  </button>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        ) : null}

        {results?.campaigns.length ? (
          <>
            <CommandSeparator />
            <CommandGroup heading="Campaigns">
              {results.campaigns.map((c: any) => (
                <CommandItem key={`c-${c.id}`} onSelect={() => go(`/campaigns/${c.id}`)} className="gap-2">
                  <Megaphone className="h-4 w-4 text-muted-foreground" />
                  <span className="truncate">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        ) : null}

        {results?.creators.length ? (
          <>
            <CommandSeparator />
            <CommandGroup heading="Creators">
              {results.creators.map((a: any) => (
                <CommandItem
                  key={`a-${a.id}`}
                  onSelect={() => go(`/brands/${a.brand_id}?tab=creators`)}
                  className="gap-2"
                >
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">@{a.tiktok_handle}</span>
                  {a.full_name && (
                    <span className="text-[10px] text-muted-foreground truncate">{a.full_name}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        ) : null}

        {results?.sessions.length ? (
          <>
            <CommandSeparator />
            <CommandGroup heading="LIVE sessions">
              {results.sessions.map((s: any) => (
                <CommandItem
                  key={`s-${s.id}`}
                  onSelect={() => go(`/live-sessions?session=${s.id}`)}
                  className="gap-2"
                >
                  <Radio className="h-4 w-4 text-muted-foreground" />
                  <span className="truncate">{s.stream_title || 'Untitled session'}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        ) : null}

        <CommandSeparator />
        <CommandGroup heading="Navigate">
          {navFiltered.slice(0, 12).map((n) => (
            <CommandItem key={n.url} onSelect={() => go(n.url)} className="gap-2">
              <n.icon className="h-4 w-4 text-muted-foreground" />
              <span>{n.title}</span>
              <span className="ml-auto text-[10px] text-muted-foreground/70 font-mono">{n.url}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      <div className="border-t px-3 py-2 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Tip: type to filter · Enter to open</span>
        <span className="flex items-center gap-2">
          <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono">⌘K</kbd>
          <span>·</span>
          <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono">⌘B</kbd> switch brand
        </span>
      </div>
    </CommandDialog>
  );
}
